import axios from 'axios';
import { Http } from '../http/http';
import { guard } from '@/utils';
import {
  ProjectDto,
  ProjectsDto,
  CreateProjectDto,
  AddEmployeeDto,
  AddPositionDto,
  EmployeeDto,
  PositionDto,
  AddEpicDto,
  EpicDto,
  AddEpicGroupDto,
  EpicGroupDto,
  ColumnDto,
  AddColumnDto,
  AddTableDto,
  TableDto,
  AddSheetDto,
  SheetDto,
  ReportDto,
  MetricDto,
  GetMetricsDto,
  GetSummaryMetricsDto,
  SummaryMetricsDto,
} from './projectsTypes';

class ProjectsHttp extends Http {
  public async exists(projectsName: string) {
    guard.notNull(projectsName, 'projectsName');
    const url = `/api/projects/exists/${encodeURI(projectsName)}`;
    const response = await axios.get<boolean>(url);
    return response.data;
  }

  public async getProjects() {
    const url = `/api/projects?filter&paging={"skip":0,"take":100}`;
    const response = await this._axios.get<ProjectsDto>(url);
    return response.data;
  }

  public async getProject(id: string) {
    guard.notNull(id, 'id');
    const url = `/api/projects/${id}`;
    const response = await this._axios.get<ProjectDto>(url);
    return response.data;
  }

  public async addProject(projectData: CreateProjectDto) {
    guard.notNull(projectData, 'projectData');
    const url = '/api/projects';
    const response = await this._axios.post<ProjectDto>(url, projectData);
    return response.data;
  }

  public async updateProject(projectData: CreateProjectDto, projectId: string) {
    guard.notNull(projectData, 'projectData');
    guard.notNull(projectId, 'projectId');
    const url = `/api/projects/${projectId}`;
    await this._axios.put(url, projectData);
  }

  public async deleteProject(id: string) {
    guard.notNull(id, 'id');
    const url = `/api/projects/${id}`;
    await this._axios.delete(url);
  }

  public async addPosition(positionData: AddPositionDto, projectId: string) {
    guard.notNull(positionData, 'positionData');
    guard.notNull(projectId, 'projectId');
    const url = `/api/projects/${projectId}/positions`;
    const response = await this._axios.post<PositionDto>(url, positionData);
    return response.data;
  }

  public async updatePosition(positionData: AddPositionDto, id: string) {
    guard.notNull(positionData, 'positionData');
    guard.notNull(id, 'id');
    const url = `/api/positions/${id}`;
    await this._axios.put(url, positionData);
  }

  public async deletePosition(id: string) {
    guard.notNull(id, 'id');
    const url = `/api/positions/${id}`;
    await this._axios.delete(url);
  }

  public async addEmployee(employeeData: AddEmployeeDto, projectId: string) {
    guard.notNull(employeeData, 'employeeData');
    guard.notNull(projectId, 'projectId');
    const url = `/api/projects/${projectId}/employees`;
    const response = await this._axios.post<EmployeeDto>(url, employeeData);
    return response.data;
  }

  public async updateEmployee(employeeData: AddEmployeeDto, id: string) {
    guard.notNull(employeeData, 'employeeData');
    guard.notNull(id, 'id');
    const url = `/api/employees/${id}`;
    await this._axios.put(url, employeeData);
  }

  public async deleteEmployee(id: string) {
    guard.notNull(id, 'id');
    const url = `/api/employees/${id}`;
    await this._axios.delete(url);
  }

  public async addEpic(epicData: AddEpicDto, projectId: string) {
    guard.notNull(epicData, 'epicData');
    guard.notNull(projectId, 'projectId');
    const url = `/api/projects/${projectId}/epics`;
    const response = await this._axios.post<EpicDto>(url, epicData);
    return response.data;
  }

  public async updateEpic(epicData: AddEpicDto, id: string) {
    guard.notNull(epicData, 'epicData');
    guard.notNull(id, 'id');
    const url = `/api/epics/${id}`;
    await this._axios.put(url, epicData);
  }

  public async deleteEpic(id: string) {
    guard.notNull(id, 'id');
    const url = `/api/epics/${id}`;
    await this._axios.delete(url);
  }

  public async addEpicGroup(epicGroupData: AddEpicGroupDto, projectId: string) {
    guard.notNull(epicGroupData, 'epicGroupData');
    guard.notNull(projectId, 'projectId');
    const url = `/api/projects/${projectId}/epicGroups`;
    const response = await this._axios.post<EpicGroupDto>(url, epicGroupData);
    return response.data;
  }

  public async updateEpicGroup(epicGroupData: AddEpicGroupDto, id: string) {
    guard.notNull(epicGroupData, 'epicGroupData');
    guard.notNull(id, 'id');
    const url = `/api/epicGroups/${id}`;
    await this._axios.put(url, epicGroupData);
  }

  public async deleteEpicGroup(id: string) {
    guard.notNull(id, 'id');
    const url = `/api/epicGroups/${id}`;
    await this._axios.delete(url);
  }

  public async addColumn(columnData: AddColumnDto, projectId: string) {
    guard.notNull(columnData, 'columnData');
    guard.notNull(projectId, 'projectId');
    const url = `/api/projects/${projectId}/columns`;
    const response = await this._axios.post<ColumnDto>(url, columnData);
    return response.data;
  }

  public async updateColumn(columnData: AddColumnDto, id: string) {
    guard.notNull(columnData, 'columnData');
    guard.notNull(id, 'id');
    const url = `/api/columns/${id}`;
    await this._axios.put(url, columnData);
  }

  public async deleteColumn(id: string) {
    guard.notNull(id, 'id');
    const url = `/api/columns/${id}`;
    await this._axios.delete(url);
  }

  public async addTable(tableData: AddTableDto, projectId: string) {
    guard.notNull(tableData, 'tableData');
    guard.notNull(projectId, 'projectId');
    const url = `/api/projects/${projectId}/tables`;
    const response = await this._axios.post<TableDto>(url, tableData);
    return response.data;
  }

  public async updateTable(tableData: AddTableDto, id: string) {
    guard.notNull(tableData, 'tableData');
    guard.notNull(id, 'id');
    const url = `/api/tables/${id}`;
    await this._axios.put(url, tableData);
  }

  public async deleteTable(id: string) {
    guard.notNull(id, 'id');
    const url = `/api/tables/${id}`;
    await this._axios.delete(url);
  }

  public async addSheet(sheetData: AddSheetDto, projectId: string) {
    guard.notNull(sheetData, 'sheetData');
    guard.notNull(projectId, 'projectId');
    const url = `/api/projects/${projectId}/sheets`;
    const response = await this._axios.post<SheetDto>(url, sheetData);
    return response.data;
  }

  public async updateSheet(sheetData: AddSheetDto, id: string) {
    guard.notNull(sheetData, 'sheetData');
    guard.notNull(id, 'id');
    const url = `/api/sheets/${id}`;
    await this._axios.put(url, sheetData);
  }

  public async deleteSheet(id: string) {
    guard.notNull(id, 'id');
    const url = `/api/sheets/${id}`;
    await this._axios.delete(url);
  }

  public async addReport(reportData: FormData, projectId: string, reportName: string) {
    guard.notNull(reportData, 'sheetData');
    guard.notNull(projectId, 'projectId');
    guard.notNull(reportName, 'reportName');
    const url = `/api/projects/${projectId}/reports?name=${reportName}`;
    const response = await this._axios.post<ReportDto>(url, reportData);
    return response.data;
  }

  public async deleteReport(id: string) {
    guard.notNull(id, 'id');
    const url = `/api/reports/${id}`;
    await this._axios.delete(url);
  }

  public async finishConstructor(id: string) {
    await this._axios.post(`/api/projects/${id}/complete`);
  }

  public async getMetrics({ id, startDate, endDate, type }: GetMetricsDto) {
    const url = `/api/project/${id}/metrics?startDate=${startDate}&endDate=${endDate}&type=${type}`;
    const response = await this._axios.get<MetricDto[]>(url);
    return response.data;
  }

  public async getSummaryMetrics({ id, startDate, endDate }: GetSummaryMetricsDto) {
    const url = `/api/project/${id}/metrics/summary?startDate=${startDate}&endDate=${endDate}`;
    const response = await this._axios.get<SummaryMetricsDto>(url);
    return response.data;
  }
}

export const projectsHttp = new ProjectsHttp();
